import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlass, Sparkle, Lightning, ArrowRight, ArrowUpRight,
  Robot, Star, Megaphone, Lock,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NICHE_CHIPS = [
  { key: 'marketing digital', labelKey: 'demo.chip_marketing' },
  { key: 'emagrecimento', labelKey: 'demo.chip_weight' },
  { key: 'trading', labelKey: 'demo.chip_trading' },
  { key: 'ingles', labelKey: 'demo.chip_english' },
  { key: 'fitness', labelKey: 'demo.chip_fitness' },
];

const scoreColor = (score) => {
  if (score >= 80) return '#10B981';
  if (score >= 60) return '#F59E0B';
  return '#EF4444';
};

const handleLogin = () => {
  const redirectUrl = window.location.origin + '/dashboard';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const SampleCard = ({ item }) => {
  const color = scoreColor(item.estimated_score);
  return (
    <div className="p-5 bg-[#0A0A0A] border border-white/10 rounded-2xl hover:border-white/20 transition" data-testid="demo-sample-card">
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="px-2 py-0.5 bg-[#2563EB]/15 text-[#3B82F6] rounded text-[10px] font-semibold uppercase tracking-wider">
              {item.type === 'vsl' ? 'VSL' : item.type === 'quiz_funnel' ? 'Quiz' : item.type === 'webinar' ? 'Webinar' : item.type === 'landing' ? 'Landing' : 'Sales Page'}
            </span>
            {item.brand_or_creator && (
              <span className="text-[10px] text-white/40 uppercase tracking-wider">{item.brand_or_creator}</span>
            )}
          </div>
          <h3 className="font-[Outfit] font-bold text-sm text-white">{item.title}</h3>
        </div>
        <div
          className="flex-shrink-0 w-12 h-12 rounded-lg flex flex-col items-center justify-center border"
          style={{ background: `${color}11`, borderColor: `${color}44` }}
        >
          <span className="text-base font-[Outfit] font-bold leading-none" style={{ color }}>{item.estimated_score}</span>
          <span className="text-[8px] uppercase tracking-wider text-white/40">score</span>
        </div>
      </div>
      <p className="text-xs text-white/60 leading-relaxed mb-3 line-clamp-3">{item.why_modelable}</p>
      {item.patterns_detected && (
        <div className="flex flex-wrap gap-1 mb-3">
          {item.patterns_detected.slice(0, 4).map((p) => (
            <span key={p} className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-[9px] text-white/50">
              {p}
            </span>
          ))}
        </div>
      )}
      {item.enriched_queries && item.enriched_queries.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-white/5">
          {item.enriched_queries.slice(0, 2).map((q, i) => (
            <a
              key={i}
              href={q.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 bg-[#1877F2]/15 hover:bg-[#1877F2]/25 border border-[#1877F2]/40 rounded text-[9px] uppercase tracking-wider font-semibold text-[#60A5FA] transition"
            >
              {q.platform === 'facebook_ads' ? '📘 FB Ads' : q.platform === 'google' ? '🔍 Google' : q.platform}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

const DiscoveryDemo = () => {
  const { t } = useTranslation();
  const [niche, setNiche] = useState('marketing digital');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchSample = useCallback(async (selectedNiche) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/discovery/sample`, {
        params: { niche: selectedNiche, country: 'BR' },
      });
      setResults(res.data);
      setNiche(selectedNiche);
    } catch (e) {
      setError(t('demo.load_fail'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  return (
    <section id="discovery-demo" className="px-6 py-24 border-t border-white/5 relative overflow-hidden" data-testid="discovery-demo-section">
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute top-1/2 left-1/4 w-[600px] h-[600px] rounded-full bg-[#A855F7]/10 blur-[140px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-[#3B82F6]/10 blur-[140px]" />
      </div>

      <div className="relative max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#A855F7]/15 border border-[#A855F7]/30 rounded-full mb-4">
            <Robot size={14} weight="fill" className="text-[#A855F7]" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-[#A855F7] font-semibold">{t('demo.badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-[Outfit] font-bold tracking-tight mb-4">
            {t('demo.title_pre')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A855F7] to-[#3B82F6]">{t('demo.title_hl')}</span><br />
            {t('demo.title_post')}
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            {t('demo.subtitle')}
            <br /><span className="text-white/40 text-sm">{t('demo.subtitle_note')}</span>
          </p>
        </div>

        {/* Niche chips */}
        <div className="flex items-center justify-center flex-wrap gap-2 mb-8">
          {NICHE_CHIPS.map((n) => (
            <button
              key={n.key}
              onClick={() => fetchSample(n.key)}
              data-testid={`demo-niche-${n.key.replace(/\s/g, '-')}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition border ${
                niche === n.key && results
                  ? 'bg-[#A855F7]/15 border-[#A855F7]/50 text-[#A855F7]'
                  : 'bg-white/[0.03] border-white/10 text-white/70 hover:border-white/20 hover:text-white'
              }`}
            >
              {t(n.labelKey)}
            </button>
          ))}
        </div>

        {/* Initial CTA / Loading / Results */}
        {!results && !loading && (
          <div className="text-center mb-8">
            <button
              onClick={() => fetchSample('marketing digital')}
              data-testid="demo-start-btn"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#A855F7] to-[#7C3AED] hover:opacity-90 rounded-xl text-base font-semibold transition shadow-2xl shadow-purple-500/30"
            >
              <MagnifyingGlass size={18} weight="bold" />
              {t('demo.show_example')}
              <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        )}

        {loading && (
          <div className="text-center p-10" data-testid="demo-loading">
            <Lightning size={28} weight="fill" className="text-[#A855F7] mx-auto mb-3 animate-pulse" />
            <p className="text-white/60 text-sm">{t('demo.loading')}</p>
          </div>
        )}

        {error && (
          <div className="text-center text-sm text-red-400 mb-4">{error}</div>
        )}

        {results && (
          <div className="space-y-5">
            <div className="flex items-center gap-2 text-sm text-white/40 mb-3">
              <Star size={14} weight="fill" className="text-[#F59E0B]" />
              {t('demo.found', { count: results.results.length })} &ldquo;{results.niche}&rdquo;
              <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] rounded">
                {t('demo.demo_tag')}
              </span>
            </div>

            {/* First 4 unlocked + paywall for rest */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {results.results.slice(0, 4).map((item, i) => (
                <SampleCard key={i} item={item} />
              ))}
            </div>

            {/* Locked overlay if more results */}
            {results.results.length > 4 && (
              <div className="relative">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-30 blur-[2px] pointer-events-none select-none">
                  {results.results.slice(4, 8).map((item, i) => (
                    <SampleCard key={i} item={item} />
                  ))}
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-gradient-to-br from-[#A855F7]/20 to-[#3B82F6]/15 backdrop-blur-xl border border-white/15 rounded-2xl p-7 max-w-md text-center shadow-2xl">
                    <Lock size={28} weight="bold" className="text-white mx-auto mb-3" />
                    <h3 className="text-xl font-[Outfit] font-bold mb-2">
                      {t('demo.locked_title', { count: results.results.length - 4 })}
                    </h3>
                    <p className="text-white/70 text-sm mb-5 leading-relaxed">
                      {t('demo.locked_pre')} <strong>{t('demo.locked_strong')}</strong> {t('demo.locked_post')}
                    </p>
                    <button
                      onClick={handleLogin}
                      data-testid="demo-unlock-cta"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:opacity-90 rounded-xl text-sm font-semibold transition shadow-lg shadow-blue-500/30"
                    >
                      <Sparkle size={14} weight="fill" />
                      {t('demo.unlock_cta')}
                      <ArrowRight size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {results.ad_library_links && results.ad_library_links.length > 0 && (
              <div className="mt-6 p-5 bg-gradient-to-br from-[#1877F2]/10 to-transparent border border-[#1877F2]/30 rounded-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <Megaphone size={18} weight="bold" className="text-[#1877F2]" />
                  <h3 className="font-[Outfit] font-bold text-sm">{t('demo.bonus_ads')}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {results.ad_library_links.map((ad, i) => (
                    <a
                      key={i}
                      href={ad.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`demo-ad-link-${i}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1877F2]/15 hover:bg-[#1877F2]/25 border border-[#1877F2]/40 rounded-lg text-xs transition"
                    >
                      {ad.label}
                      <ArrowUpRight size={12} weight="bold" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default DiscoveryDemo;
